import { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import TradeRuleCard from './trade-rule-card';
import {
  TradeRule,
  TradeAction,
  TradeRules,
} from '@/modules/trade-rule-builder/model/trade-rule-builder';
import {
  serializeRules,
  deserializeRules,
  updateCondition,
  updateRuleField,
  updateGroupOperator,
  addCondition,
  addGroup,
  deleteConditionFromGroup,
  deleteGroupFromGroup,
} from '@/modules/trade-rule-builder/util/trade-rule-builder-util';
import { preferencesApi } from '@/core/preferences/export';

export default function TradeRuleBuilder() {
  const [rules, setRules] = useState<TradeRule[]>([]);

  useEffect(() => {
    (async () => {
      const pref_rules = await preferencesApi.getPreferences<TradeRules>('trade-rules');
      if (pref_rules?.rules?.length) {
        const loaded = deserializeRules(pref_rules.rules);
        setRules(loaded);
      }
    })();
  }, []);

  const handleSaveAll = async () => {
    const rawRules = serializeRules(rules);
    await preferencesApi.upsertPreferences<TradeRules>('trade-rules', { rules: rawRules });
  };

  const handleSaveToFile = () => {
    const rawRules = serializeRules(rules);
    const blob = new Blob([JSON.stringify({ rules: rawRules })], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trade-rules.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const parsed = JSON.parse(e.target?.result as string);
        // Accepts either { rules: [...] } or just an array
        const ruleArray = Array.isArray(parsed) ? parsed : parsed.rules;
        if (!Array.isArray(ruleArray)) throw new Error('Invalid rules format');
        const loaded = deserializeRules(ruleArray);
        setRules(loaded);
      } catch (err) {
        console.error('Failed to load rules:', err);
      }
    };
    reader.readAsText(file);
  };

  const onDeleteRule = (ruleId: string) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    setRules(prev => prev.filter(rule => rule.id !== ruleId));
  };

  const onAddAction = (ruleId: string) => {
    setRules(prev =>
      prev.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              actions: [
                ...rule.actions,
                {
                  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  action: 'buy',
                  volume: 100,
                  symbol: '',
                },
              ],
            }
          : rule,
      ),
    );
  };

  const onUpdateAction = <K extends keyof TradeAction>(
    ruleId: string,
    actionId: string,
    key: K,
    value: TradeAction[K],
  ) => {
    setRules(prev =>
      prev.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              actions: rule.actions.map(a => (a.id === actionId ? { ...a, [key]: value } : a)),
            }
          : rule,
      ),
    );
  };

  const onDeleteAction = (ruleId: string, actionId: string) => {
    setRules(prev =>
      prev.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              actions: rule.actions.filter(a => a.id !== actionId),
            }
          : rule,
      ),
    );
  };

  return (
    <div className="d-flex flex-column h-100">
      <div className="sticky-top bg-light border-bottom p-2 d-flex align-items-center gap-2 z-3">
        <Button size="sm" onClick={handleSaveAll}>
          Save All
        </Button>
        <input
          type="file"
          accept="application/json"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          id="loadRulesInput"
        />
        <Button size="sm" onClick={handleSaveToFile}>
          Save To File
        </Button>
        <Button size="sm" onClick={() => document.getElementById('loadRulesInput')?.click()}>
          Load From File
        </Button>
      </div>

      <div className="flex-grow-1 overflow-auto p-2">
        {rules.map(rule => (
          <TradeRuleCard
            key={rule.id}
            rule={rule}
            onUpdateRuleField={(ruleId, key, value) =>
              updateRuleField(ruleId, key as keyof TradeRule, value, setRules)
            }
            onUpdateCondition={(groupId, conditionId, key, value) =>
              updateCondition(rule.id, groupId, conditionId, key, value, setRules)
            }
            onUpdateGroupOperator={(groupId, value) =>
              updateGroupOperator(rule.id, groupId, value, setRules)
            }
            onAddCondition={groupId => addCondition(rule.id, groupId, setRules)}
            onAddGroup={groupId => addGroup(rule.id, groupId, setRules)}
            onDeleteCondition={(groupId, conditionId) =>
              setRules(prev =>
                prev.map(r =>
                  r.id === rule.id
                    ? {
                        ...r,
                        rootGroup: deleteConditionFromGroup(r.rootGroup, groupId, conditionId),
                      }
                    : r,
                ),
              )
            }
            onDeleteGroup={groupId =>
              setRules(prev =>
                prev.map(r =>
                  r.id === rule.id
                    ? {
                        ...r,
                        rootGroup: deleteGroupFromGroup(r.rootGroup, groupId),
                      }
                    : r,
                ),
              )
            }
            onAddAction={onAddAction}
            onUpdateAction={onUpdateAction}
            onDeleteAction={onDeleteAction}
            onDeleteRule={onDeleteRule}
          />
        ))}

        <div className="text-center p-2 border-top">
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setRules(prev => [
                ...prev,
                {
                  id: `${Date.now()}`,
                  timestamp: new Date().toISOString().slice(0, 19),
                  eventId: '',
                  eventName: '',
                  eventCountry: '',
                  actions: [
                    {
                      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                      action: 'buy',
                      volume: 100,
                      symbol: '',
                    },
                  ],
                  rootGroup: { id: `${Date.now()}-grp`, operator: 'AND', children: [] },
                },
              ])
            }
          >
            + New Rule
          </Button>
        </div>
      </div>
    </div>
  );
}
